import { useState, useRef, useEffect } from 'react';

const SAMPLE_CLEARANCES = [
  {
    label: 'IFR to Lancaster',
    text: 'Cessna 172 November 4 7 2 1 Tango, cleared to Lancaster Airport via direct Harrisburg VOR then Victor 2 1 0, maintain 3000, expect 6000 ten minutes after departure, departure frequency 124.85, squawk 4721.',
  },
  {
    label: 'IFR to Chicago Midway',
    text: 'Piper Cherokee November 8 8 6 Papa Alpha, cleared to Chicago Midway Airport via radar vectors then direct Joliet VOR, maintain 4000, expect 8000 five minutes after departure, departure frequency 119.35, squawk 3305.',
  },
  {
    label: 'IFR to Boston Logan',
    text: 'Cessna 182 November 2 2 4 Kilo Bravo, cleared to Boston Logan International via direct Providence VOR then Victor 3, maintain 5000, expect 9000 fifteen minutes after departure, departure frequency 128.4, squawk 5512.',
  },
];

interface ParsedClearance {
  clearedTo: string;
  route: string;
  initialAltitude: string;
  expectAltitude: string;
  departureFreq: string;
  squawk: string;
  callsign: string;
}

function parseClearance(text: string): ParsedClearance | null {
  if (!text.trim()) return null;

  const t = text;

  // Callsign — first noun phrase before "cleared"
  const callsignMatch = t.match(/^([^,]+?),?\s*cleared/i);
  const callsign = callsignMatch ? callsignMatch[1].trim() : '—';

  // Cleared to
  const clearedToMatch = t.match(/cleared to ([^,]+(?:Airport|International|Regional|Field)?[^,]*)/i);
  const clearedTo = clearedToMatch ? clearedToMatch[1].trim() : '—';

  // Route — via ... maintain
  const routeMatch = t.match(/via ([^,]+(?:,\s*[^,]+)*?)(?:,\s*maintain|\s+maintain)/i);
  const route = routeMatch ? routeMatch[1].trim() : '—';

  // Initial altitude
  const altMatch = t.match(/maintain (\d[\d,]*)/i);
  const initialAltitude = altMatch ? `${parseInt(altMatch[1].replace(/,/g, '')).toLocaleString()} ft` : '—';

  // Expect altitude
  const expectMatch = t.match(/expect (\d[\d,]*)/i);
  const expectAltitude = expectMatch ? `${parseInt(expectMatch[1].replace(/,/g, '')).toLocaleString()} ft` : '—';

  // Departure frequency
  const freqMatch = t.match(/departure frequency ([\d.]+)/i);
  const departureFreq = freqMatch ? `${freqMatch[1]} MHz` : '—';

  // Squawk
  const squawkMatch = t.match(/squawk (\d{4})/i);
  const squawk = squawkMatch ? squawkMatch[1] : '—';

  return { callsign, clearedTo, route, initialAltitude, expectAltitude, departureFreq, squawk };
}

function buildReadback(parsed: ParsedClearance): string {
  if (!parsed) return '';
  return `Cleared to ${parsed.clearedTo}, via ${parsed.route}, maintain ${parsed.initialAltitude}, expect ${parsed.expectAltitude}, departure ${parsed.departureFreq}, squawk ${parsed.squawk}. ${parsed.callsign}.`;
}

const FIELDS = [
  { key: 'callsign', label: 'Callsign', icon: 'ri-plane-line' },
  { key: 'clearedTo', label: 'Cleared To', icon: 'ri-map-pin-2-line' },
  { key: 'route', label: 'Route', icon: 'ri-route-line' },
  { key: 'initialAltitude', label: 'Initial Altitude', icon: 'ri-arrow-up-line' },
  { key: 'expectAltitude', label: 'Expect Altitude', icon: 'ri-arrow-up-double-line' },
  { key: 'departureFreq', label: 'Departure Freq', icon: 'ri-radio-line' },
  { key: 'squawk', label: 'Squawk', icon: 'ri-broadcast-line' },
] as const;

export default function FeatureTryItLive() {
  const [input, setInput] = useState('');
  const [parsed, setParsed] = useState<ParsedClearance | null>(null);
  const [parsing, setParsing] = useState(false);
  const [visibleFields, setVisibleFields] = useState<number>(0);
  const [copied, setCopied] = useState(false);
  const [confidence] = useState(() => Math.floor(Math.random() * 4) + 95);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runParse = (text: string) => {
    if (!text.trim()) return;
    setParsed(null);
    setVisibleFields(0);
    setParsing(true);

    setTimeout(() => {
      const result = parseClearance(text);
      setParsed(result);
      setParsing(false);
    }, 900);
  };

  useEffect(() => {
    if (!parsed) return;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setVisibleFields(i);
      if (i >= FIELDS.length) clearInterval(interval);
    }, 120);
    return () => clearInterval(interval);
  }, [parsed]);

  const handleInput = (val: string) => {
    setInput(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!val.trim()) { setParsed(null); setVisibleFields(0); return; }
    timerRef.current = setTimeout(() => runParse(val), 600);
  };

  const loadSample = (sample: typeof SAMPLE_CLEARANCES[0]) => {
    setInput(sample.text);
    runParse(sample.text);
  };

  const handleCopy = () => {
    if (!parsed) return;
    navigator.clipboard.writeText(buildReadback(parsed));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    setInput('');
    setParsed(null);
    setVisibleFields(0);
  };

  return (
    <section id="try-it-live" className="py-16 sm:py-24 bg-[#060E1E] relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'linear-gradient(#00FFD1 1px, transparent 1px), linear-gradient(90deg, #00FFD1 1px, transparent 1px)', backgroundSize: '48px 48px' }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#00FFD1]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-3 sm:px-4 py-1.5 mb-4 sm:mb-5">
            <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse inline-block" />
            <span className="text-[#00FFD1] text-[10px] sm:text-xs font-semibold tracking-widest uppercase">Live Demo</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4 tracking-tight px-4">
            Try It <span className="text-[#00FFD1]">Live</span>
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto px-4">
            Paste any ATC clearance below and watch WingmanFix parse it into a structured readback in real time.
          </p>
        </div>

        {/* Sample buttons - wrap to 2-per-row on mobile */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
          {SAMPLE_CLEARANCES.map((s) => (
            <button
              key={s.label}
              onClick={() => loadSample(s)}
              className="flex items-center gap-1.5 sm:gap-2 bg-white/5 hover:bg-[#00FFD1]/10 border border-white/10 hover:border-[#00FFD1]/40 text-gray-300 hover:text-[#00FFD1] px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-text-line text-xs sm:text-sm" />
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden">{s.label.replace('IFR to ', '')}</span>
            </button>
          ))}
        </div>

        {/* Input/Output panels - stack vertically on mobile */}
        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 items-start">
          {/* Input panel */}
          <div className="bg-[#0D1F3C] border border-white/10 rounded-2xl overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-3.5 border-b border-white/10 bg-[#0A1628]">
              <div className="flex items-center gap-2 sm:gap-2.5">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg bg-[#00A3E0]/20">
                  <i className="ri-mic-line text-[#00A3E0] text-sm sm:text-base" />
                </div>
                <span className="text-white text-xs sm:text-sm font-semibold">ATC Clearance Input</span>
              </div>
              <div className="flex items-center gap-2">
                {/* Waveform decoration - hidden on mobile */}
                <div className="hidden sm:flex items-end gap-[3px] h-5">
                  {[3,5,7,4,6,8,5,3,6,4].map((h, i) => (
                    <div
                      key={i}
                      className="w-[3px] rounded-full bg-[#00A3E0]/50"
                      style={{ height: `${h * 2}px` }}
                    />
                  ))}
                </div>
                <span className="text-[10px] text-[#00A3E0] font-mono bg-[#00A3E0]/10 px-2 py-0.5 rounded-full">124.85 MHz</span>
              </div>
            </div>

            {/* Textarea */}
            <div className="p-4 sm:p-5">
              <textarea
                value={input}
                onChange={(e) => handleInput(e.target.value)}
                placeholder="Paste or type an ATC clearance here…&#10;&#10;e.g. Cessna 172 November 4721 Tango, cleared to Lancaster Airport via direct Harrisburg VOR then Victor 210, maintain 3000…"
                className="w-full h-40 sm:h-52 bg-[#060E1E] border border-white/10 rounded-xl p-3 sm:p-4 text-gray-200 text-xs sm:text-sm font-mono placeholder-gray-600 resize-none focus:outline-none focus:border-[#00FFD1]/40 transition-colors leading-relaxed"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-gray-600 text-[10px] sm:text-xs font-mono">{input.length} chars</span>
                <div className="flex items-center gap-2">
                  {input && (
                    <button
                      onClick={handleClear}
                      className="text-gray-500 hover:text-gray-300 text-[10px] sm:text-xs px-2 sm:px-3 py-1.5 rounded-lg hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line mr-1" />Clear
                    </button>
                  )}
                  <button
                    onClick={() => runParse(input)}
                    disabled={!input.trim() || parsing}
                    className="flex items-center gap-1.5 sm:gap-2 bg-[#00FFD1] hover:bg-[#00e6bc] disabled:opacity-40 disabled:cursor-not-allowed text-[#060E1E] font-bold text-xs sm:text-sm px-3 sm:px-5 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                  >
                    {parsing ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Parsing…
                      </>
                    ) : (
                      <>
                        <i className="ri-flashlight-line" />
                        Parse Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Tip */}
            <div className="mx-4 sm:mx-5 mb-4 sm:mb-5 flex items-start gap-2 sm:gap-3 bg-[#00A3E0]/5 border border-[#00A3E0]/20 rounded-xl p-3 sm:p-3.5">
              <i className="ri-information-line text-[#00A3E0] text-sm sm:text-base mt-0.5 shrink-0" />
              <p className="text-gray-400 text-[10px] sm:text-xs leading-relaxed">
                WingmanFix listens to live ATC audio in-flight and parses clearances automatically — no typing required. This demo shows the parsing engine in action.
              </p>
            </div>
          </div>

          {/* Output panel */}
          <div className="bg-[#0D1F3C] border border-white/10 rounded-2xl overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-3.5 border-b border-white/10 bg-[#0A1628]">
              <div className="flex items-center gap-2 sm:gap-2.5">
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg bg-[#00FFD1]/20">
                  <i className="ri-cpu-line text-[#00FFD1] text-sm sm:text-base" />
                </div>
                <span className="text-white text-xs sm:text-sm font-semibold">Parsed Clearance</span>
              </div>
              {parsed && (
                <div className="flex items-center gap-1.5 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full px-2 sm:px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00FFD1] animate-pulse inline-block" />
                  <span className="text-[#00FFD1] text-[10px] sm:text-xs font-semibold">{confidence}% confidence</span>
                </div>
              )}
            </div>

            <div className="p-4 sm:p-5">
              {/* Empty state */}
              {!parsed && !parsing && (
                <div className="flex flex-col items-center justify-center h-40 sm:h-52 text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-white/5 mb-3 sm:mb-4">
                    <i className="ri-radar-line text-gray-500 text-xl sm:text-2xl" />
                  </div>
                  <p className="text-gray-500 text-xs sm:text-sm">Parsed fields will appear here</p>
                  <p className="text-gray-600 text-[10px] sm:text-xs mt-1">Type or paste a clearance on the left</p>
                </div>
              )}

              {/* Parsing animation */}
              {parsing && (
                <div className="flex flex-col items-center justify-center h-40 sm:h-52 gap-3 sm:gap-4">
                  <div className="relative w-12 h-12 sm:w-14 sm:h-14">
                    <div className="absolute inset-0 rounded-full border-2 border-[#00FFD1]/20 animate-ping" />
                    <div className="absolute inset-0 rounded-full border-2 border-[#00FFD1]/40 animate-pulse" />
                    <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full bg-[#00FFD1]/10">
                      <i className="ri-cpu-line text-[#00FFD1] text-lg sm:text-xl" />
                    </div>
                  </div>
                  <div className="text-center px-4">
                    <p className="text-[#00FFD1] text-xs sm:text-sm font-semibold">Analyzing clearance…</p>
                    <p className="text-gray-500 text-[10px] sm:text-xs mt-1">Extracting route, altitude, frequency</p>
                  </div>
                </div>
              )}

              {/* Parsed fields */}
              {parsed && !parsing && (
                <div className="space-y-2 sm:space-y-2.5">
                  {FIELDS.map((field, idx) => (
                    <div
                      key={field.key}
                      className="flex items-center gap-2 sm:gap-3 bg-[#060E1E] border border-white/8 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 transition-all duration-300"
                      style={{
                        opacity: idx < visibleFields ? 1 : 0,
                        transform: idx < visibleFields ? 'translateX(0)' : 'translateX(12px)',
                        transition: 'opacity 0.25s ease, transform 0.25s ease',
                      }}
                    >
                      <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg bg-[#00FFD1]/10 shrink-0">
                        <i className={`${field.icon} text-[#00FFD1] text-xs sm:text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 text-[10px] uppercase tracking-widest font-semibold">{field.label}</p>
                        <p className="text-white text-xs sm:text-sm font-medium truncate mt-0.5">
                          {parsed[field.key] || '—'}
                        </p>
                      </div>
                      {idx < visibleFields && (
                        <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-[#00FFD1] text-xs sm:text-sm" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Readback panel - full width on mobile */}
            {parsed && !parsing && visibleFields >= FIELDS.length && (
              <div className="mx-4 sm:mx-5 mb-4 sm:mb-5 bg-gradient-to-br from-[#00FFD1]/10 to-[#00A3E0]/5 border border-[#00FFD1]/30 rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2 sm:mb-2.5">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <i className="ri-mic-2-line text-[#00FFD1] text-xs sm:text-sm" />
                    <span className="text-[#00FFD1] text-[10px] sm:text-xs font-bold uppercase tracking-widest">Readback Ready</span>
                  </div>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 px-2 sm:px-3 py-1 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className={copied ? 'ri-check-line text-[#00FFD1]' : 'ri-file-copy-line'} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-white text-xs sm:text-sm font-mono leading-relaxed">
                  "{buildReadback(parsed)}"
                </p>
                <div className="flex items-center gap-2 mt-2.5 sm:mt-3 pt-2.5 sm:pt-3 border-t border-white/10">
                  <i className="ri-shield-check-line text-[#00FFD1] text-[10px] sm:text-xs" />
                  <span className="text-gray-400 text-[10px] sm:text-xs">ATC transcript captured · {confidence}% accuracy</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom stats - collapse to 2-col on mobile */}
        <div className="mt-8 sm:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[
            { icon: 'ri-timer-flash-line', value: '< 1s', label: 'Parse Time' },
            { icon: 'ri-percent-line', value: '97%', label: 'Field Accuracy' },
            { icon: 'ri-hand-heart-line', value: '100%', label: 'Hands-Free' },
            { icon: 'ri-error-warning-line', value: '0', label: 'Missed Readbacks' },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2 sm:gap-3 bg-[#0D1F3C] border border-white/8 rounded-xl px-3 sm:px-5 py-3 sm:py-4">
              <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-[#00FFD1]/10 shrink-0">
                <i className={`${stat.icon} text-[#00FFD1] text-base sm:text-lg`} />
              </div>
              <div>
                <p className="text-white text-lg sm:text-xl font-bold leading-none">{stat.value}</p>
                <p className="text-gray-500 text-[10px] sm:text-xs mt-0.5">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}